import type {
  AuthenticatedUser,
  CarePlan,
  FollowUp,
  Intervention,
  MedicationManagementReport,
  MedicationRelatedProblem,
  MtmAssessment,
  MtmAssessmentType,
  Patient,
  PatientAllergy,
  PatientCondition,
  PatientMedication,
  Pharmacist,
  Service,
} from "@dosa/types";
import type {
  CreateCarePlanInput,
  CreateInterventionInput,
  CreateMrpInput,
  MedicationQuestionnaireInput,
  PatientAllergyInput,
  PatientConditionInput,
  PatientMedicationInput,
  ScheduleFollowUpInput,
} from "@dosa/validation";
import { evaluateAllClinicalFlags, type PatientClinicalContext } from "@dosa/clinical";
import { db } from "./mock-db";
import { newId, nowIso, todayIso } from "./id";

/**
 * Local demo API client — implements the same shape the real Worker API (apps/worker) would
 * expose, but reads/writes the in-memory mock-db instead of issuing authenticated fetch()
 * calls. This lets the full patient -> pharmacist -> report -> follow-up flow run without a
 * deployed Cloudflare Worker, D1 database, or authentik instance. See docs/ARCHITECTURE.md.
 */

const DELAY_MS = 150;
function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), DELAY_MS));
}

// ---------- auth (demo role switcher) ----------

export async function getCurrentUser(userId: string): Promise<AuthenticatedUser | null> {
  const user = db.users.find((u) => u.id === userId);
  if (!user) return delay(null);
  const patient = db.patients.find((p) => p.userId === user.id);
  const pharmacist = db.pharmacists.find((p) => p.userId === user.id);
  return delay({
    id: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    role: user.role,
    status: user.status,
    preferredLocale: user.preferredLocale,
    patientId: patient?.id,
    pharmacistId: pharmacist?.id,
  });
}

// ---------- patient profile ----------

export async function getPatient(patientId: string): Promise<Patient | null> {
  return delay(db.patients.find((p) => p.id === patientId) ?? null);
}

export async function updatePatientProfile(patientId: string, input: Partial<Patient>): Promise<Patient> {
  const patient = db.patients.find((p) => p.id === patientId);
  if (!patient) throw new Error("Patient not found");
  Object.assign(patient, input, { updatedAt: nowIso() });
  return delay(patient);
}

// ---------- medications / conditions / allergies ----------

export async function listPatientMedications(patientId: string): Promise<PatientMedication[]> {
  return delay(db.patientMedications.filter((m) => m.patientId === patientId));
}

export async function addPatientMedication(patientId: string, input: PatientMedicationInput): Promise<PatientMedication> {
  const record: PatientMedication = {
    id: newId("pmd"),
    patientId,
    status: "ACTIVE",
    source: "PATIENT_REPORTED",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
  };
  db.patientMedications.push(record);
  return delay(record);
}

export async function listPatientConditions(patientId: string): Promise<PatientCondition[]> {
  return delay(db.patientConditions.filter((c) => c.patientId === patientId));
}

export async function addPatientCondition(patientId: string, input: PatientConditionInput): Promise<PatientCondition> {
  const record: PatientCondition = {
    id: newId("pcn"),
    patientId,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
    status: input.status ?? "ACTIVE",
  };
  db.patientConditions.push(record);
  return delay(record);
}

export async function listPatientAllergies(patientId: string): Promise<PatientAllergy[]> {
  return delay(db.patientAllergies.filter((a) => a.patientId === patientId));
}

export async function addPatientAllergy(patientId: string, input: PatientAllergyInput): Promise<PatientAllergy> {
  const record: PatientAllergy = {
    id: newId("pal"),
    patientId,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
    severity: input.severity,
  };
  db.patientAllergies.push(record);
  return delay(record);
}

// ---------- services & appointments ----------

export async function listServices(): Promise<Service[]> {
  return delay(db.services.filter((s) => s.isActive));
}

export async function listPatientAppointments(patientId: string) {
  return delay(db.appointments.filter((a) => a.patientId === patientId));
}

export async function bookAppointment(patientId: string, input: { serviceId: string; scheduledAt: string; reasonForVisit?: string }) {
  const service = db.services.find((s) => s.id === input.serviceId);
  if (!service) throw new Error("Service not found");
  const pharmacist = db.pharmacists[0];
  const appointment = {
    id: newId("apt"),
    patientId,
    pharmacistId: pharmacist?.id,
    serviceId: service.id,
    type: service.appointmentType,
    status: "CONFIRMED" as const,
    channel: "VIDEO" as const,
    scheduledAt: new Date(input.scheduledAt).toISOString(),
    durationMinutes: service.durationMinutes,
    reasonForVisit: input.reasonForVisit,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  db.appointments.push(appointment);
  return delay(appointment);
}

export async function cancelAppointment(appointmentId: string, reason: string) {
  const appt = db.appointments.find((a) => a.id === appointmentId);
  if (!appt) throw new Error("Appointment not found");
  appt.status = "CANCELLED";
  appt.cancelledReason = reason;
  appt.updatedAt = nowIso();
  return delay(appt);
}

// ---------- MTM intake ----------

export async function submitMedicationQuestionnaire(patientId: string, input: MedicationQuestionnaireInput): Promise<MtmAssessment> {
  let assessment = db.mtmAssessments.find((a) => a.patientId === patientId && a.status === "DRAFT");
  const pharmacist = db.pharmacists[0];
  if (!assessment) {
    assessment = {
      id: newId("mtm"),
      patientId,
      pharmacistId: pharmacist?.id ?? "",
      type: "INITIAL_MTM",
      status: "DRAFT",
      startedAt: nowIso(),
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    db.mtmAssessments.push(assessment);
  }
  assessment.medicationQuestionnaire = { ...input, respondedAt: nowIso() };
  assessment.updatedAt = nowIso();
  return delay(assessment);
}

// ---------- pharmacist: patients & clinical context ----------

export async function listAssignedPatients(_pharmacistId: string): Promise<Patient[]> {
  return delay(db.patients);
}

export async function getPatientClinicalFlags(patientId: string) {
  const activeMedications = db.patientMedications.filter((m) => m.patientId === patientId && m.status === "ACTIVE");
  const allergies = db.patientAllergies.filter((a) => a.patientId === patientId);
  const openHighSeverityMrps = db.mrps.filter((m) => m.patientId === patientId && m.status !== "RESOLVED");
  const upcomingFollowUps = db.followUps.filter((f) => f.patientId === patientId);
  const hasMonitoringPlan = db.carePlans.some((c) => c.patientId === patientId && c.monitoringParameters.length > 0);
  const ctx: PatientClinicalContext = {
    patientId,
    activeMedications,
    allergies,
    hasMonitoringPlan,
    openHighSeverityMrps,
    upcomingFollowUps,
  };
  return delay(evaluateAllClinicalFlags(ctx));
}

// ---------- MTM assessment workflow ----------

export async function startMtmAssessment(patientId: string, pharmacistId: string, type: MtmAssessmentType): Promise<MtmAssessment> {
  const existingDraft = db.mtmAssessments.find((a) => a.patientId === patientId && a.status !== "COMPLETED" && a.status !== "CANCELLED");
  if (existingDraft) {
    existingDraft.status = "IN_PROGRESS";
    existingDraft.pharmacistId = pharmacistId;
    existingDraft.updatedAt = nowIso();
    return delay(existingDraft);
  }
  const assessment: MtmAssessment = {
    id: newId("mtm"),
    patientId,
    pharmacistId,
    type,
    status: "IN_PROGRESS",
    startedAt: nowIso(),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  db.mtmAssessments.push(assessment);
  return delay(assessment);
}

export async function getMtmAssessment(id: string): Promise<MtmAssessment | null> {
  return delay(db.mtmAssessments.find((a) => a.id === id) ?? null);
}

export async function listMtmAssessments(patientId: string): Promise<MtmAssessment[]> {
  return delay(db.mtmAssessments.filter((a) => a.patientId === patientId));
}

export async function updateMtmAssessment(id: string, patch: Partial<MtmAssessment>): Promise<MtmAssessment> {
  const assessment = db.mtmAssessments.find((a) => a.id === id);
  if (!assessment) throw new Error("Assessment not found");
  Object.assign(assessment, patch, { updatedAt: nowIso() });
  return delay(assessment);
}

export async function completeMtmAssessment(id: string): Promise<MtmAssessment> {
  return updateMtmAssessment(id, { status: "COMPLETED", completedAt: nowIso() });
}

export async function listMrps(assessmentId: string): Promise<MedicationRelatedProblem[]> {
  return delay(db.mrps.filter((m) => m.mtmAssessmentId === assessmentId));
}

export async function addMrp(patientId: string, pharmacistId: string, input: CreateMrpInput): Promise<MedicationRelatedProblem> {
  const mrp: MedicationRelatedProblem = {
    id: newId("mrp"),
    patientId,
    identifiedByPharmacistId: pharmacistId,
    status: "IDENTIFIED",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
  };
  db.mrps.push(mrp);
  return delay(mrp);
}

export async function resolveMrp(id: string, status: MedicationRelatedProblem["status"], resolutionNotes?: string): Promise<MedicationRelatedProblem> {
  const mrp = db.mrps.find((m) => m.id === id);
  if (!mrp) throw new Error("MRP not found");
  mrp.status = status;
  mrp.resolutionNotes = resolutionNotes;
  if (status === "RESOLVED") mrp.resolvedAt = nowIso();
  mrp.updatedAt = nowIso();
  return delay(mrp);
}

export async function listInterventions(assessmentId: string): Promise<Intervention[]> {
  return delay(db.interventions.filter((i) => i.mtmAssessmentId === assessmentId));
}

export async function addIntervention(patientId: string, pharmacistId: string, input: CreateInterventionInput): Promise<Intervention> {
  const intervention: Intervention = {
    id: newId("ivt"),
    patientId,
    pharmacistId,
    status: "PROPOSED",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
  };
  db.interventions.push(intervention);
  return delay(intervention);
}

export async function getCarePlan(assessmentId: string): Promise<CarePlan | null> {
  return delay(db.carePlans.find((c) => c.mtmAssessmentId === assessmentId) ?? null);
}

export async function createCarePlan(patientId: string, pharmacistId: string, input: CreateCarePlanInput): Promise<CarePlan> {
  const carePlan: CarePlan = {
    id: newId("cp"),
    patientId,
    pharmacistId,
    status: "ACTIVE",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    monitoringParameters: input.monitoringParameters.map((p) => ({ id: newId("mp"), ...p })),
    medicationActionPlan: input.medicationActionPlan.map((a) => ({ id: newId("ai"), isCompleted: false, ...a })),
    mtmAssessmentId: input.mtmAssessmentId,
    summary: input.summary,
    summaryAr: input.summaryAr,
    patientEducationTopics: input.patientEducationTopics,
    patientEducationTopicsAr: input.patientEducationTopicsAr,
  };
  db.carePlans.push(carePlan);
  return delay(carePlan);
}

export async function scheduleFollowUp(patientId: string, pharmacistId: string, input: ScheduleFollowUpInput): Promise<FollowUp> {
  const followUp: FollowUp = {
    id: newId("fu"),
    patientId,
    pharmacistId,
    status: "SCHEDULED",
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...input,
  };
  db.followUps.push(followUp);
  return delay(followUp);
}

export async function listFollowUps(patientId: string): Promise<FollowUp[]> {
  return delay(db.followUps.filter((f) => f.patientId === patientId));
}

export async function completeFollowUp(id: string, outcomeNotes: string): Promise<FollowUp> {
  const followUp = db.followUps.find((f) => f.id === id);
  if (!followUp) throw new Error("Follow-up not found");
  followUp.status = "COMPLETED";
  followUp.outcomeNotes = outcomeNotes;
  followUp.completedAt = nowIso();
  followUp.updatedAt = nowIso();
  return delay(followUp);
}

// ---------- reports ----------

export async function generateMedicationManagementReport(assessmentId: string): Promise<MedicationManagementReport> {
  const assessment = db.mtmAssessments.find((a) => a.id === assessmentId);
  if (!assessment) throw new Error("Assessment not found");
  const patient = db.patients.find((p) => p.id === assessment.patientId);
  const pharmacist = db.pharmacists.find((p) => p.id === assessment.pharmacistId);
  if (!patient || !pharmacist) throw new Error("Related patient or pharmacist not found");

  const medications = db.patientMedications.filter((m) => m.patientId === patient.id);
  const mrps = db.mrps.filter((m) => m.mtmAssessmentId === assessmentId);
  const interventions = db.interventions.filter((i) => i.mtmAssessmentId === assessmentId);
  const carePlan = db.carePlans.find((c) => c.mtmAssessmentId === assessmentId);
  const followUp = db.followUps.find((f) => f.mtmAssessmentId === assessmentId);

  return delay({
    id: newId("rpt"),
    generatedAt: nowIso(),
    assessment,
    patient: {
      id: patient.id,
      patientCode: patient.patientCode,
      firstName: patient.firstName,
      lastName: patient.lastName,
      firstNameAr: patient.firstNameAr,
      lastNameAr: patient.lastNameAr,
      dateOfBirth: patient.dateOfBirth,
      biologicalSex: patient.biologicalSex,
    },
    pharmacist: {
      id: pharmacist.id,
      firstName: pharmacist.firstName,
      lastName: pharmacist.lastName,
      firstNameAr: pharmacist.firstNameAr,
      lastNameAr: pharmacist.lastNameAr,
      licenseNumber: pharmacist.licenseNumber,
    },
    medications,
    medicationRelatedProblems: mrps,
    interventions,
    carePlan,
    monitoringParameters: carePlan?.monitoringParameters ?? [],
    actionItems: carePlan?.medicationActionPlan ?? [],
    followUp,
  });
}

// ---------- admin ----------

export async function listUsers() {
  return delay(db.users);
}
export async function listPharmacists(): Promise<Pharmacist[]> {
  return delay(db.pharmacists);
}
export async function listAllAppointments() {
  return delay(db.appointments);
}
export async function getBusinessKpis() {
  return delay({
    totalPatients: db.patients.length,
    totalPharmacists: db.pharmacists.length,
    appointmentsThisMonth: db.appointments.length,
    completedMtmAssessmentsThisMonth: db.mtmAssessments.filter((a) => a.status === "COMPLETED").length,
    newPatientsThisMonth: db.patients.length,
    activeCarePlans: db.carePlans.filter((c) => c.status === "ACTIVE").length,
  });
}
export async function getClinicalKpis() {
  const open = db.mrps.filter((m) => m.status !== "RESOLVED");
  return delay({
    openMrpCount: open.length,
    mrpsByCategory: {
      INDICATION: open.filter((m) => m.category === "INDICATION").length,
      EFFECTIVENESS: open.filter((m) => m.category === "EFFECTIVENESS").length,
      SAFETY: open.filter((m) => m.category === "SAFETY").length,
      ADHERENCE: open.filter((m) => m.category === "ADHERENCE").length,
    },
    interventionsThisMonth: db.interventions.length,
    followUpsDueThisWeek: db.followUps.filter((f) => f.status === "SCHEDULED" && f.dueDate <= todayIso()).length,
    followUpsOverdue: db.followUps.filter((f) => f.status === "SCHEDULED" && f.dueDate < todayIso()).length,
  });
}
